'use client';

import { useEffect, useState } from 'react';
import { FileText, Package } from 'lucide-react';

type UploadedFile = {
  fileName: string;
  url: string;
  designData?: {
    dataURL?: string;
  } | null;
};

type Props = {
  uploadedFile?: UploadedFile | null;
  productThumbnail?: string;
  productName: string;
  /** Width/height of the ordered print, so an uploaded photo is shown cropped like the print. */
  printAspectRatio?: number | null;
};

export { printAspectRatioFromOptions } from '@/lib/cart/print-aspect-ratio';

declare global {
  interface Window {
    pdfjsLib?: any;
  }
}

const PDF_JS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

function isPdfFile(file?: UploadedFile | null) {
  const fileName = file?.fileName || '';
  const url = file?.url || '';
  return /\.pdf(?:$|\?)/i.test(fileName) || /\.pdf(?:$|\?)/i.test(url);
}

function toAbsoluteUrl(url: string) {
  if (/^https?:\/\//i.test(url)) return url;
  return new URL(url, window.location.origin).toString();
}

function loadPdfJs(): Promise<any> {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);

  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${PDF_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.pdfjsLib), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = PDF_JS_SRC;
    script.async = true;
    script.onload = () => resolve(window.pdfjsLib);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export function CartItemPreview({ uploadedFile, productThumbnail, productName, printAspectRatio }: Props) {
  const savedPreview = uploadedFile?.designData?.dataURL || null;
  const [pdfPreview, setPdfPreview] = useState<string | null>(savedPreview);
  const [imageFailed, setImageFailed] = useState(false);
  const pdfFile = isPdfFile(uploadedFile);

  useEffect(() => {
    let cancelled = false;

    if (savedPreview) {
      setPdfPreview(savedPreview);
      return;
    }

    if (!uploadedFile?.url || !pdfFile) {
      setPdfPreview(null);
      return;
    }

    async function renderPdfThumbnail() {
      try {
        const pdfjsLib = await loadPdfJs();
        if (!pdfjsLib) throw new Error('PDF.js not loaded');
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

        const pdf = await pdfjsLib.getDocument(toAbsoluteUrl(uploadedFile!.url)).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1 });
        const maxSize = 220;
        const scale = Math.min(maxSize / viewport.width, maxSize / viewport.height);
        const scaledViewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(scaledViewport.width);
        canvas.height = Math.ceil(scaledViewport.height);
        const context = canvas.getContext('2d');
        if (!context) throw new Error('Canvas context missing');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: context, viewport: scaledViewport }).promise;

        if (!cancelled) setPdfPreview(canvas.toDataURL('image/png'));
      } catch (error) {
        console.error('Failed to render cart PDF preview:', error);
        if (!cancelled) setPdfPreview(null);
      }
    }

    renderPdfThumbnail();

    return () => {
      cancelled = true;
    };
  }, [pdfFile, savedPreview, uploadedFile?.url]);

  if (savedPreview || pdfPreview) {
    return (
      <img
        src={savedPreview || pdfPreview || ''}
        alt={uploadedFile?.fileName || productName}
        className="h-full w-full object-contain"
      />
    );
  }

  if (uploadedFile?.url && !pdfFile && !imageFailed && printAspectRatio) {
    const wide = printAspectRatio >= 1;
    return (
      <div className="flex h-full w-full items-center justify-center bg-white">
        <div
          className={`overflow-hidden ring-1 ring-gray-300 ${wide ? 'w-full' : 'h-full'}`}
          style={{ aspectRatio: String(printAspectRatio) }}
        >
          <img
            src={uploadedFile.url}
            alt={uploadedFile.fileName}
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
        </div>
      </div>
    );
  }

  if (uploadedFile?.url && !pdfFile && !imageFailed) {
    return (
      <img
        src={uploadedFile.url}
        alt={uploadedFile.fileName}
        className="h-full w-full object-cover"
        onError={() => setImageFailed(true)}
      />
    );
  }

  if (pdfFile) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-gray-500">
        <FileText className="h-7 w-7" />
        <span className="text-[10px] font-medium uppercase">PDF</span>
      </div>
    );
  }

  if (productThumbnail && !imageFailed) {
    return (
      <img
        src={productThumbnail}
        alt={productName}
        className="h-full w-full object-cover"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center text-gray-400">
      <Package className="h-8 w-8" />
    </div>
  );
}
